import * as Sentry from "@sentry/react";
import { QueryClientProvider } from "@tanstack/react-query";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { env } from "@/env";
import { queryClient } from "./config/query";
import {
  appInsightReactPlugin,
  initAppInsights,
  initSentry,
} from "./features/instruments";
// Krevd av designsystemet for å støtte popover i eldre nettlesere
import "@oddbird/popover-polyfill";

import "./config/i18n.ts";
import "./styles/index.css";

import { AppInsightsContext } from "@microsoft/applicationinsights-react-js";
import { BestillingProvider } from "@/features/bestillinger";
import { AuthProvider } from "./features/auth";
import { InnerApp } from "./InnerApp.tsx";
import { router } from "./Router.ts";

// initialize before rendering to capture initial page view
initSentry(router, env.sentryUrl);
initAppInsights();

const rootElement = document.getElementById("root");
if (!rootElement) {
  throw new Error("Root element not found");
}

const root = createRoot(rootElement, {
  // Callback called when an error is thrown and not caught by an ErrorBoundary.
  onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
    console.warn("Uncaught error", error, errorInfo.componentStack);
  }),
  // Callback called when React catches an error in an ErrorBoundary.
  onCaughtError: Sentry.reactErrorHandler(),
  // Callback called when React automatically recovers from errors.
  onRecoverableError: Sentry.reactErrorHandler(),
});

root.render(
  <StrictMode>
    <Sentry.ErrorBoundary
      fallback={
        <div className="flex flex-col h-screen w-screen items-center justify-center p-4">
          <h1>Norgepris for fjernvarme</h1>
          <div role="alert" data-color="danger" className="ds-alert">
            Det oppsto en feil. Vennligst last siden på nytt eller kontakt
            støtte.
          </div>
        </div>
      }
    >
      <AppInsightsContext.Provider value={appInsightReactPlugin}>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <BestillingProvider>
              <InnerApp router={router} />
            </BestillingProvider>
          </AuthProvider>
        </QueryClientProvider>
      </AppInsightsContext.Provider>
    </Sentry.ErrorBoundary>
  </StrictMode>,
);
